import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type ProgramInclusion, type UnlockedStreamsEntry } from 'ProgramInclusion';
import { transformKeyCase } from 'Utils/transformKeyCase';
import {
    type FilterOption,
    type FilterOptionsArgs,
    type FilterOptionsByType,
    type FilterTypeAlias,
    type GetReportResponse,
    type ReportMeta,
    type ReportRecord,
    type ReportResult,
} from './Reports.types';

// No casing conversion: the report record and the filter options are snake_case on both sides, and
// update_unlocked_streams must send its camelCase entries unchanged.
const baseConfig = getBackRoyalApiBaseConfig('reportsApi', { convertCasing: false });

type CreateReportResponse = {
    contents: { reports: ReportResult[] };
    meta?: ReportMeta;
};

type FilterOptionsResponse = {
    // In maintenance mode, the server sends `reports: []` and no filter_options.
    contents: { filter_options?: { filter_type: FilterTypeAlias; options: FilterOption[] }[] };
};

export const reportsApi = createApi('reportsApi', {
    ...baseConfig,
    endpoints: builder => ({
        // A POST that reads. Each Refresh runs it again (see useReportState), as a lazy query.
        getReport: builder.query<GetReportResponse, ReportRecord & { fields: string[] }>({
            query: record => ({ method: 'POST', url: 'reports.json', body: { record } }),
            transformResponse: (response: CreateReportResponse) => ({
                report: response.contents.reports[0],
                meta: response.meta ?? {},
            }),
        }),

        getFilterOptions: builder.query<FilterOptionsByType, FilterOptionsArgs>({
            query: ({ reportType, institutionId }) => ({
                url: 'reports/filter_options.json',
                params: { report_type: reportType, institution_id: institutionId },
            }),
            transformResponse: (response: FilterOptionsResponse) =>
                Object.fromEntries(
                    (response.contents.filter_options ?? []).map(({ filter_type, options }) => [filter_type, options]),
                ),
        }),

        // Admin only. The server stores the entries in camelCase (see
        // ProgramInclusion#stream_has_been_manually_unlocked?), so this endpoint lives in an api without
        // casing conversion.
        updateUnlockedStreams: builder.mutation<
            ProgramInclusion[],
            { programInclusionId: string; unlockedStreams: UnlockedStreamsEntry[] }
        >({
            query: ({ programInclusionId, unlockedStreams }) => ({
                method: 'POST',
                url: 'admin_actions/program_inclusion/update_unlocked_streams.json',
                body: { record_id: programInclusionId, unlocked_streams: unlockedStreams },
            }),
            transformResponse: (response: { contents: { user: { program_inclusions: unknown[] } } }) =>
                (
                    transformKeyCase(response.contents.user, { to: 'camelCase' }) as {
                        programInclusions: ProgramInclusion[];
                    }
                ).programInclusions,
            extraOptions: { maxRetries: 0 },
        }),
    }),
});

export const { useLazyGetReportQuery, useGetReportQuery, useGetFilterOptionsQuery, useUpdateUnlockedStreamsMutation } =
    reportsApi;
